import { createContext, useCallback, useContext, useEffect } from 'react';

export interface BreadcrumbSegment {
  id: string;
  title: string;
  href?: string;
  order: number;
}

interface BreadcrumbContextType {
  segments: BreadcrumbSegment[];
  registerSegment: (segment: BreadcrumbSegment) => void;
  unregisterSegment: (id: string) => void;
  updateSegment: (id: string, updates: Partial<BreadcrumbSegment>) => void;
}

export const BreadcrumbContext = createContext<BreadcrumbContextType | null>(
  null,
);

export const useBreadcrumb = () => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider');
  }
  return context;
};

export const useBreadcrumbSegment = (
  id: string,
  title: string,
  order: number,
  href?: string,
) => {
  const { registerSegment, unregisterSegment, updateSegment } = useBreadcrumb();

  useEffect(() => {
    registerSegment({ id, title, order, href });
    return () => unregisterSegment(id);
  }, [id, title, order, href, registerSegment, unregisterSegment]);

  const updateTitle = useCallback(
    (newTitle: string) => {
      updateSegment(id, { title: newTitle });
    },
    [id, updateSegment],
  );

  const updateHref = useCallback(
    (newHref: string) => {
      updateSegment(id, { href: newHref });
    },
    [id, updateSegment],
  );

  return { updateTitle, updateHref };
};
